<template>
  <div class="campaign-list">
    <div class="campaign-list__header">
      <div>
        <h1 class="page-title">Campañas Instantáneas</h1>
        <p class="page-subtitle">Gestiona las campañas de correo instantáneo para clientes</p>
      </div>
      <div class="campaign-list__actions">
        <b-button
          variant="primary"
          @click="$router.push({ name: 'instant-campaign-create' })"
        >
          <i class="fas fa-plus mr-2"></i>
          Nueva Campaña Instantánea
        </b-button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" label="Cargando..."></b-spinner>
      <p class="mt-2 text-muted">Cargando campañas...</p>
    </div>

    <!-- Error -->
    <b-alert v-if="error" show variant="danger" dismissible @dismissed="clearError">
      {{ error }}
    </b-alert>

    <!-- Tabla -->
    <div v-if="!loading" class="table-container">
      <b-table
        :items="campanasFiltradas"
        :fields="fields"
        hover
        :per-page="perPage"
        :current-page="currentPage"
        show-empty
        empty-text="No hay campañas instantáneas configuradas"
        class="campanas-table"
        thead-class="table-header"
      >
        <!-- Nombre -->
        <template #cell(nombre)="data">
          <span class="campaign-name">{{ data.item.nombre }}</span>
        </template>

        <!-- Fecha Envío -->
        <template #cell(fecha_envio)="data">
          <span class="stat-value">
            {{ data.item.fecha_envio ? new Date(data.item.fecha_envio).toLocaleDateString('es-CL') : '—' }}
          </span>
        </template>

        <!-- Tipo OT -->
        <template #cell(tipo_servicio)="data">
          <span
            v-if="data.item.tipo_servicio"
            :class="['tipo-ot-badge', tipoOtBadgeClass(data.item.tipo_servicio)]"
          >
            <font-awesome-icon
              v-if="data.item.tipo_servicio === 'GARANTIA'"
              :icon="['fas', 'shield-alt']"
            />
            <font-awesome-icon v-else :icon="['fas', 'file-alt']" />
            <span>{{ getTipoOTLabel(data.item.tipo_servicio) }}</span>
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <!-- Enviados -->
        <template #cell(total_enviados)="data">
          <span class="stat-value">{{ data.item.total_enviados || 0 }}</span>
        </template>

        <!-- Apertura % -->
        <template #cell(apertura)="data">
          <span :class="['stat-value', getAperturaClass(data.item.apertura)]">
            {{ data.item.apertura != null ? data.item.apertura + '%' : '0%' }}
          </span>
        </template>

        <!-- Clics % -->
        <template #cell(clics)="data">
          <span class="stat-value">
            {{ data.item.clics != null ? data.item.clics + '%' : '0%' }}
          </span>
        </template>

        <!-- Rebote % -->
        <template #cell(rebote)="data">
          <span class="stat-value">
            {{ data.item.rebote != null ? data.item.rebote + '%' : '0%' }}
          </span>
        </template>

        <!-- Acciones -->
        <template #cell(acciones)="data">
          <b-dropdown
            right
            variant="warning"
            size="sm"
            class="acciones-dropdown"
            toggle-class="acciones-btn"
            no-caret
          >
            <template #button-content>
              <font-awesome-icon :icon="['fas', 'cog']" class="mr-1" />
              Acciones
            </template>
            <b-dropdown-item @click="verMetricas(data.item.id)">
              <font-awesome-icon :icon="['fas', 'chart-bar']" class="mr-2" /> Ver Métricas
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <b-pagination
        v-if="totalRows > perPage"
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="center"
        class="mt-3"
      ></b-pagination>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'InstantCampaignList',
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      fields: [
        { key: 'nombre', label: 'Nombre de la Campaña', sortable: true, thClass: 'th-nombre' },
        { key: 'fecha_envio', label: 'Fecha Envío', sortable: true },
        { key: 'tipo_servicio', label: 'Tipo de OT', sortable: true },
        { key: 'total_enviados', label: 'Enviados', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'apertura', label: 'Apertura %', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'clics', label: 'Clics %', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'rebote', label: 'Rebote %', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'acciones', label: 'Acciones', sortable: false, thClass: 'text-center', tdClass: 'text-center' },
      ],
    };
  },
  computed: {
    ...mapGetters('InstantCampaign', ['historial', 'loading', 'error']),
    campanasFiltradas() {
      return this.historial;
    },
    totalRows() {
      return this.campanasFiltradas.length;
    },
  },
  created() {
    this.fetchHistorial();
  },
  methods: {
    ...mapActions('InstantCampaign', ['fetchHistorial', 'clearError']),
    getTipoOTLabel(tipo) {
      const labels = { PRESUPUESTO: 'Presupuesto', GARANTIA: 'Garantía', TODAS: 'Todas' };
      return labels[tipo] || tipo;
    },
    tipoOtBadgeClass(tipo) {
      if (tipo === 'GARANTIA') return 'badge-garantia';
      if (tipo === 'PRESUPUESTO') return 'badge-presupuesto';
      if (tipo === 'TODAS') return 'badge-todas';
      return 'badge-presupuesto';
    },
    verMetricas(id) {
      this.$router.push({ name: 'instant-campaign-metrics', params: { id } });
    },
    getAperturaClass(tasa) {
      if (tasa >= 50) return 'text-green';
      if (tasa >= 25) return 'text-warning';
      return 'text-danger';
    },
  },
};
</script>

<style scoped>
.text-green {
  color: rgba(23, 219, 148, 1);
}

.campaign-list {
  padding: 24px;
  background: #F5F6FA;
  min-height: calc(100vh - 60px);
}

.campaign-list__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.campaign-list__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #000;
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.table-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: visible;
}

::v-deep .table-responsive {
  overflow: visible;
}

::v-deep .campanas-table {
  font-size: 14px;
  color: #1F2937;
}

::v-deep .campanas-table .table-header th {
  background: #DDDDE1;
  color: #374151;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 2px solid #E5E7EB;
  padding: 12px 16px;
  white-space: nowrap;
}

::v-deep .campanas-table tbody tr td {
  padding: 14px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #F3F4F6;
}

::v-deep .campanas-table tbody tr:hover td {
  background: #FAFAFA;
}

.campaign-name {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
}

.tipo-ot-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-presupuesto {
  background: #29B6F6 !important;
  color: #000 !important;
}

.badge-garantia {
  background: #4CAF50 !important;
  color: #fff !important;
}

.badge-todas {
  background: #26C6DA !important;
  color: #000 !important;
}

.badge-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

.stat-value {
  font-size: 14px;
  font-weight: 500;
}

::v-deep .acciones-dropdown .acciones-btn {
  background: #FECE2F;
  border-color: #FECE2F;
  color: #23201C;
  font-weight: 600;
  font-size: 13px;
  padding: 5px 12px;
  border-radius: 6px;
}

::v-deep .acciones-dropdown .acciones-btn:hover,
::v-deep .acciones-dropdown .acciones-btn:focus {
  background: #f5c400;
  border-color: #f5c400;
  color: #23201C;
  box-shadow: none;
}

::v-deep .acciones-dropdown .dropdown-menu {
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 140px;
}
</style>
